import {
	CREATE_SNIPPET_FAILURE_REASON,
	type SnippetCreateFailureReason,
} from '../../constants/snippetAnalytics.ts';
import type { SnippetBodyFormat } from '../../types/snippetBodyFormat.ts';
import type { SnippetProduct } from '../../types/snippetProduct.ts';

export class SnippetError extends Error {
	statusCode?: number;

	constructor(message: string) {
		super(`Snippet error: ${message}`);
	}
}

const BODY_TOO_LARGE_PATTERN = /body must be \d+\s*KB or less/i;

/**
 * Message thrown by `blockServiceClient.createSnippet` when the response can't be
 * transformed into a Snippet. Shared so the throw site and `classifyCreateError`
 * can't drift apart.
 */
export const CREATE_TRANSFORM_FAILED_MESSAGE = 'Unable to transform snippet data after creation';

/**
 * Returns true when the error is a 400 SnippetError caused by the template body exceeding the size limit.
 */
export const isBodyTooLargeError = (err: unknown): boolean =>
	err instanceof SnippetError && err.statusCode === 400 && BODY_TOO_LARGE_PATTERN.test(err.message);

/**
 * Classifies a create failure into a categorical `reason` (+ `statusCode` for
 * HTTP errors). Never returns the raw message (avoids leaking PII/UGC).
 */
export const classifyCreateError = (
	err: unknown,
): { reason: SnippetCreateFailureReason; statusCode?: number } => {
	if (isBodyTooLargeError(err)) {
		return {
			reason: CREATE_SNIPPET_FAILURE_REASON.BODY_TOO_LARGE,
			statusCode: (err as SnippetError).statusCode,
		};
	}

	if (err instanceof SnippetError) {
		return {
			reason: CREATE_SNIPPET_FAILURE_REASON.CREATE_EXCEPTION,
			...(err.statusCode !== undefined && { statusCode: err.statusCode }),
		};
	}

	if (err instanceof Error && err.message === CREATE_TRANSFORM_FAILED_MESSAGE) {
		return { reason: CREATE_SNIPPET_FAILURE_REASON.TRANSFORM_FAILED };
	}

	return { reason: CREATE_SNIPPET_FAILURE_REASON.UNKNOWN };
};

/** Payload for creating or updating a snippet */
export type CreateSnippetPayload = {
	name: string;
	body: string;
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
	description?: string;
};

export type UpdateSnippetPayload = {
	name: string;
	body: string;
	description?: string | null;
};

export type SnippetFavoriteData = {
	userId: string;
	snippetIds: string[]; // Array of snippet ARIs
};
